import { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, RootStateOrAny } from 'react-redux';
import { useHistory } from 'react-router';

import { HomeOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import { Breadcrumb, Card, Table, Typography } from 'antd';

import notifier from 'utils/notifier';
import { sortByIPA, getDisplayCompanyId, handleDownload, formatDate } from 'utils/helpers';
import { ErrorResponse, UserModel } from 'utils/types';
import { Application } from 'utils/enums';

import { getAuthEnabledProviderList, GetAuthEnabledProviderListResponse, GetFaxStatusResponse } from 'store/authorization/provider/actions';
import { getStatus } from 'store/authorization/faxes/actions';
import { get as getFax } from 'store/authorization/faxes/actions';
import { getLetterInfo } from 'store/authorization/letter/actions';
import { downloadFileFromBlob } from "store/authorization/files/actions";
import { getHelpCenterLinks } from 'store/app/helpCenter/actions';

import SearchFaxForm from 'components/Auth/SearchFaxForm';
import AuthDetail from 'components/Auth/Detail';
import HelpCenterLink from 'components/Common/HelpCenterLink';

import styles from './SearchFax.module.scss';

const { Title } = Typography;

interface SearchFaxPageProps {
  getAuthEnabledProviderList(userModel: UserModel): any;
  getStatus(): any,
  getFax(params: Object): any,
  getLetterInfo(authNo: string, companyId: string): any;
  downloadFileFromBlob(companyId: string, application: string, filepath: string): any;
  isFetchingFaxes: boolean,
  isFetchingAuthLetter: boolean,
  getHelpCenterLinks(companyId: string, page: string, type: string): any;
};

const SearchFaxAuthPage = (props: SearchFaxPageProps) => {

  const history = useHistory();

  const [providerList, setProviderList] = useState([] as any);
  const [faxStatusList, setFaxStatusList] = useState([] as GetFaxStatusResponse[]);
  const [searchResult, setSearchResult] = useState([]);
  const [tableInfo, setTableInfo] = useState({});
  const [totalFaxResults, setTotalFaxResults] = useState(0);
  const [state, setState] = useState('start');
  const [authDetail, setAuthDetail] = useState({});
  const [expandDataSource, setExpandDataSource] = useState<Object[]>([]);
  const [collapseState, setCollapseState] = useState<any>([]);
  const [helpCenter, setHelpCenter] = useState<any>([]);

  const columns = [
    {
        title: 'IPA',
        key: 'RmiCompanyId',
        dataIndex: 'RmiCompanyId',
        width: "10%",
        render: (text: string) => getDisplayCompanyId(text)
    },
    {
        title: 'Auth Number',
        key: 'RmiAuthNo',
        dataIndex: 'RmiAuthNo',
        width: "35%",
        sorter: (a: any, b: any) => a.RmiAuthNo.localeCompare(b.RmiAuthNo),
        render: (text: string, record: any) => <a href='#/' onClick={() => triggerAuthDetailPage({companyId: record.RmiCompanyId, authNo: text})}>{text}</a>,
    },
    {
        title: 'Member ID',
        key: 'RmiMembId',
        dataIndex: 'RmiMembId',
        width: "15%",
        sorter: (a: any, b: any) => a.RmiMembId.localeCompare(b.RmiMembId),
    },
    {
        title: 'Member Name',
        key: 'RmiMembName',
        dataIndex: 'RmiMembName',
        width: "20%",
        sorter: (a: any, b: any) => a.RmiMembName.localeCompare(b.RmiMembName),
    },
    {
        title: 'Refer To Provider',
        key: 'RPRmiRevFullName',
        dataIndex: 'RPRmiRevFullName',
        width: "20%",
        sorter: (a: any, b: any) => a.RPRmiRevFullName.localeCompare(b.RPRmiRevFullName),
    },
  ];

  const expandColumns = [
    {
      title: "Document Name",
      key: "RmiFileName",
      dataIndex: "RmiFileName",
      width: "50%",
      render: (_: string, record: any) => <a onClick={() => downloadFileFromBlob(record)}>{record.RmiFileName}</a>
    },
    {
      title: "Upload Date",
      key: "RmiFileDate",
      dataIndex: "RmiFileDate",
      width: "50%",
      render: (_: string, record: any) => formatDate(record.RmiFileDate, 'MM/DD/YYYY')
    },
  ];

  const DEFAULT_PAGE_SIZE = 20;

  const getLetterInfo = (authNo: string, companyId: string) => {
    props.getLetterInfo(authNo, companyId)
      .then((response: any) => {
        setExpandDataSource(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const handleRowExpand = (expanded: any, record: any) => {
    const keys = [];

    if (expanded) {
      getLetterInfo(record.RmiAuthNo, record.RmiCompanyId);
      keys.push(record.key);
    }
    setCollapseState(keys);
  }

  const downloadFileFromBlob = (documentFile: any) => {
    notifier.success(`Start downloading ${documentFile.RmiFileName}`)
    props.downloadFileFromBlob(sessionStorage.getItem('ipaGroup')!, Application.ProviderPortal, documentFile.RmiFileLocation)
    .then((response: any) => {
      handleDownload(response, documentFile.RmiFileName);
    })
    .catch((err: ErrorResponse) => {
      notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
    });
  }

  const NewTableDataHandler = (data: any) => {

    if (data.length > 0 && data[0].RmiCount) {
      setTotalFaxResults(data[0].RmiCount);
    }

    setSearchResult(data);
  };

  const triggerAuthDetailPage = (data: any) => {
    setState('auth-detail');
    setAuthDetail(data);
  }

  const triggerStartPage = () => {
    setState('start');
  }

  const authEnabledProviderListParam = {
    UserId: sessionStorage.user_id,
    UserRole: sessionStorage.user_role
  };

  const getHelpCenterLinks = () => {
    props.getHelpCenterLinks(sessionStorage.getItem('ipaGroup')!, '/auth/letter-search', 'page')
      .then((response: any) => {
        setHelpCenter(response);
    })
    .catch((err: ErrorResponse) => {
      console.log(err?.response?.data?.Message);
    });
  }

  useEffect(() => {
    props.getAuthEnabledProviderList(authEnabledProviderListParam)
    .then((response: GetAuthEnabledProviderListResponse) => {
      setProviderList(sortByIPA(response, 'Provider'))
    })
    .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data.Message || 'Something went wrong, please contact a system administrator.');
    });

    props.getStatus()
    .then((response: GetFaxStatusResponse[]) => {
      const IPA = sessionStorage.getItem('ipa');
      setFaxStatusList(response.filter(status => status.RmiCompanyId === IPA));
    })
    .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data.Message || 'Something went wrong, please contact a system administrator.');
    });

    getHelpCenterLinks();
  }, []);

  return(
    <div className={styles['search-authorization-letter-page']}>
      <div className={styles['header-title']}>
        <Title level={4} className="ml-3">Search Authorization Letters</Title>
      </div>
      <div>
        <Breadcrumb>
          <Breadcrumb.Item>
            <HomeOutlined
              onClick={() => history.push('/quality/overview')}
            />
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <SearchOutlined />
            <a href="#" onClick={triggerStartPage}>Search Authorization Letters</a>
          </Breadcrumb.Item>
          {
            state == 'auth-detail' &&
            <Breadcrumb.Item >
            <UserOutlined />
              <a href="#" onClick={triggerAuthDetailPage}>Authorization Details</a>
            </Breadcrumb.Item>
          }
        </Breadcrumb>
        {
          helpCenter?.length > 0 ? helpCenter.map((hc: any) => {
            return (
              <>
                <div className={`row ${styles['help-center-link']}`}>
                  <div className="col-md-24">
                    <HelpCenterLink
                      clickEventId={hc.ClickEventId}
                      linkName={hc.LinkName}
                      pagePath={hc.PagePath}
                      url={hc.Link}
                      companyId={hc.CompanyId}
                    />
                  </div>
                </div>
              </>
            );
          }) : null
        }
      </div>

      {state === 'start' && (
        <div>
          <div className={styles['search-section']}>
            <SearchFaxForm
              getFax={props.getFax}
              onTableChange={tableInfo}
              newTableDataHandler={NewTableDataHandler}
              providerList={providerList}
              faxStatusList={faxStatusList}
              isFetchingFaxes={props.isFetchingFaxes}
              setCollapseState={setCollapseState}
            />
          </div>
          {searchResult && searchResult.length !== 0 && (
            <div className={styles['content']}>
              <Table
                columns={columns}
                dataSource= {searchResult}
                pagination={{
                  position: ['bottomCenter'],
                  showSizeChanger: true,
                  defaultPageSize: DEFAULT_PAGE_SIZE,
                  total: totalFaxResults,
                  hideOnSinglePage: true,
                }}
                onChange={(paginationConfig) => {
                  setCollapseState([]);
                  setTableInfo(paginationConfig);
                }}
                expandable={{
                  expandedRowRender: record =>
                    (<>
                      <Card style={{ width: '100%' }}>
                        <Table
                          columns={expandColumns}
                          dataSource={expandDataSource}
                          loading={props.isFetchingAuthLetter}
                          pagination={false}
                        />
                      </Card>
                  </>),
                }}
                onExpand={(expanded, record) => handleRowExpand(expanded, record)}
                expandedRowKeys={collapseState}
                loading={props.isFetchingFaxes}
              />
            </div>
          )}
        </div>
      )}
      {state === 'auth-detail' && (
        <AuthDetail
          providerList={providerList}
          authDetail={authDetail}
          triggerStartPage={triggerStartPage}
        />
      )}
    </div>
  );
}


const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingFaxes: store.rootReducer.authFaxes.isFetching,
  isFetchingAuthLetter: store.rootReducer.authLetter.isFetching,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    getAuthEnabledProviderList,
    getStatus,
    getFax,
    getLetterInfo,
    downloadFileFromBlob,
    getHelpCenterLinks,
  },
  dispatch,
);
export default connect(mapStateToProps, mapDispatchToProps)(SearchFaxAuthPage);